import { ArrowRight, Check } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { scopeSummary } from "@/lib/setup/format";
import type { ConceptScope } from "@/types/concept";


export function ConceptSoFar({ name, onRename, angleLabel, scope }: {
  name: string;
  onRename: (name: string) => void;
  /** Facets users only; null hides the angle row. */
  angleLabel: string | null;
  scope: ConceptScope | null | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const save = () => { if (draft.trim()) onRename(draft.trim()); setEditing(false); };
  // Still to do on ①: the angle row (when shown) is the only one that's done.
  const rows: [string, string][] = [
    ["Seeds", "3–5 papers to start from"],
    ["Scope", scopeSummary(scope)],
    ["Papers to grade", "Estimated once you pick seeds"],
  ];
  return (
    <aside className="flex flex-col gap-5 rounded-xl border bg-muted/30 p-5" aria-label="Your concept so far">
      <p className="text-sm font-medium text-muted-foreground">Your concept so far</p>
      <div className="flex items-center gap-2">
        {editing ? (
          <input autoFocus aria-label="Concept name" value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            onBlur={save} maxLength={200} className="flex-1 rounded-md border bg-background px-2 py-1 text-lg font-semibold" />
        ) : (
          <>
            <span className="flex-1 text-lg font-semibold">{name || "Untitled concept"}</span>
            <Button variant="ghost" size="sm" onClick={() => { setDraft(name); setEditing(true); }}>Rename</Button>
          </>
        )}
      </div>
      {angleLabel !== null && (
        <div className="flex gap-3">
          <Marker done={!!angleLabel} />
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">1 angle</span>
            <span className="w-fit rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-sm">{angleLabel || "Your angle"}</span>
          </div>
        </div>
      )}
      <dl className="flex flex-col gap-3">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-3">
            <Marker done={false} />
            <div><dt className="text-sm text-muted-foreground">{k}</dt><dd>{v}</dd></div>
          </div>
        ))}
      </dl>
      <ol className="-mx-5 -mb-5 mt-auto flex flex-wrap items-center gap-1 rounded-b-xl border-t bg-background px-5 py-3 text-sm text-muted-foreground" aria-label="How PaperChase works">
        {["Frame", "Curate", "Analyze", "Build"].map((s, i) => (
          <li key={s} className="flex items-center gap-1">
            {i > 0 && <ArrowRight aria-hidden className="size-3" />}
            <span className={i === 0 ? "font-medium text-foreground" : undefined}>{s}</span>
          </li>
        ))}
      </ol>
    </aside>
  );
}

/** Green ✓ = done; dashed ring = still to do (mockup board ①). */
function Marker({ done }: { done: boolean }) {
  return done ? (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-chart-2 text-background">
      <Check aria-hidden className="size-3" /><span className="sr-only">Done</span>
    </span>
  ) : (
    <span className="size-5 shrink-0 rounded-full border border-dashed border-muted-foreground">
      <span className="sr-only">To do</span>
    </span>
  );
}
